import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import saveOcsTranslationSettings from '../services/saveOcsTranslationSettings';
import setTranslationSettings from '../actions/setTranslationSettings';
import store from '../store/store';
import storeOutlookOptions from './storeOutlookOptions';
import TranslationSettings from '../store/schema/TranslationSettings';
import TranslationMode from '../store/schema/TranslationMode';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isSuccessStatusCode } from 'owa-http-status-codes';
import { logUsage, PerformanceDatapoint, DatapointStatus } from 'owa-analytics';
import {
    TRANSLATION_MODE_SETTING_NAME,
    TARGET_LANGUAGE_SETTING_NAME,
    EXCLUDED_LANGUAGES_SETTING_NAME,
} from '../services/loadOcsTranslationSettings';

interface LanguageTag {
    languageTag: string;
}

export default async function updateTranslationSettings(
    newSettings: TranslationSettings
): Promise<boolean> {
    const settingsToUpdate = [];
    const scope = getUserConfiguration().SessionSettings.UserEmailAddress;

    // Log a data point if translation mode changes
    if (store.settings.translationMode !== newSettings.translationMode) {
        createTranslationModeUpdate(settingsToUpdate, newSettings.translationMode, scope);
        logUsage('InlineTranslationChangeTranslationMode', [], {
            cosmosOnlyData: newSettings.translationMode.toString(),
        });
    }

    let exclusionChanged = false;
    // Log a data point if target language changes
    if (store.settings.targetLanguage !== newSettings.targetLanguage) {
        createTargetLanguageUpdate(settingsToUpdate, newSettings.targetLanguage, scope);

        // If the new target language is not part of exclusion list, we need to add it.
        if (newSettings.excludedLanguages.indexOf(newSettings.targetLanguage) < 0) {
            newSettings.excludedLanguages.push(newSettings.targetLanguage);
            exclusionChanged = true;
        }

        logUsage('InlineTranslationChangeTargetLanguage', [], {
            cosmosOnlyData: newSettings.targetLanguage ? newSettings.targetLanguage : 'none',
        });
    }

    // Log a data point for every language opted out of
    newSettings.excludedLanguages.forEach(newLanguage => {
        if (!store.settings.excludedLanguages.includes(newLanguage)) {
            exclusionChanged = true;
            logUsage('InlineTranslationOptOutOfLanguage', [], { cosmosOnlyData: newLanguage });
        }
    });

    // Log a data point for every language with opt out removed
    store.settings.excludedLanguages.forEach(originalLanguage => {
        if (!newSettings.excludedLanguages.includes(originalLanguage)) {
            exclusionChanged = true;
            logUsage('InlineTranslationRemoveOptOutOfLanguage', [], {
                cosmosOnlyData: originalLanguage,
            });
        }
    });

    if (exclusionChanged) {
        createExcludedLanguagesUpdate(settingsToUpdate, newSettings.excludedLanguages, scope);
    }

    if (settingsToUpdate.length > 0) {
        let state = 'SavingOcsSettings';
        try {
            if (isFeatureEnabled('tra-useOcsSettings')) {
                const response = await saveOcsTranslationSettings(settingsToUpdate);
                if (!response || !isSuccessStatusCode(response.status)) {
                    throw new Error('Failed to save settings in OCS');
                }
            }
            state = 'SavingOoSettings';
            await storeOutlookOptions(newSettings);
        } catch (ex) {
            const error = ex instanceof Error ? ex : new Error(`Error while ${state}`);
            const datapoint = new PerformanceDatapoint('InlineTranslationUpdateSettings');
            datapoint.endWithError(DatapointStatus.ServerError, error);
            return false;
        }
        setTranslationSettings(newSettings);
    }
    return true;
}

export function createTranslationModeUpdate(
    settingUpdates: any[],
    value: TranslationMode,
    scope: string
) {
    settingUpdates.push({
        name: TRANSLATION_MODE_SETTING_NAME,
        value,
        type: 'Int',
        scope,
    });
}

export function createTargetLanguageUpdate(settingUpdates: any[], value: string, scope: string) {
    const language = value == null ? '' : value;
    settingUpdates.push({
        name: TARGET_LANGUAGE_SETTING_NAME,
        value: language,
        type: 'String',
        scope,
    });
}

export function createExcludedLanguagesUpdate(
    settingUpdates: any[],
    value: string[],
    scope: string
) {
    const newLanguages: LanguageTag[] = [];
    value.forEach(lang => newLanguages.push({ languageTag: lang }));
    settingUpdates.push({
        name: EXCLUDED_LANGUAGES_SETTING_NAME,
        value: JSON.stringify(newLanguages),
        type: 'JSON',
        scope,
    });
}
